import axios from 'axios';
import { clearAuthSession, getStoredAuthToken, getStoredAuthUser } from '../utils/authStorage';

const ACTIVE_ORGANIZATION_HEADER = 'X-Active-Organization-Id';

type StoredUserContext = {
  active_organization_id?: number | null;
  role?: string | null;
};

function getStoredActiveOrganizationId(): number | null {
  try {
    const parsedUser = getStoredAuthUser<StoredUserContext>();
    if (!parsedUser) {
      return null;
    }

    const activeOrganizationId = Number(parsedUser?.active_organization_id || 0);
    return activeOrganizationId > 0 ? activeOrganizationId : null;
  } catch {
    return null;
  }
}

// Configure axios base URL for FastAPI backend
const DEFAULT_API_URL = '/';

function isLoopbackHost(hostname?: string | null): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || hostname === '[::1]';
}

function resolveApiBaseUrl(): string {
  const configuredBase = ((typeof window !== 'undefined' && window._env_?.REACT_APP_API_URL)
    || process.env.REACT_APP_API_URL
    || '')
    .trim();

  if (typeof window !== 'undefined') {
    const currentHostname = window.location.hostname;
    const currentProtocol = window.location.protocol === 'https:' ? 'https:' : 'http:';

    if (configuredBase) {
      if (configuredBase.startsWith('/')) {
        return configuredBase.replace(/\/$/, '');
      }

      try {
        const parsed = new URL(configuredBase);
        if (isLoopbackHost(currentHostname) && isLoopbackHost(parsed.hostname)) {
          parsed.hostname = currentHostname;
          parsed.protocol = currentProtocol;
        }
        return parsed.toString().replace(/\/$/, '');
      } catch {
        return configuredBase;
      }
    }

    if (isLoopbackHost(currentHostname)) {
      return `${currentProtocol}//${currentHostname}:8000`;
    }
  }

  return configuredBase || DEFAULT_API_URL;
}

const API_BASE_URL = resolveApiBaseUrl();
const SAFE_NETWORK_ERROR_MESSAGE = 'Unable to complete the request right now. Please try again shortly or contact support if the issue continues.';
const AUTH_NOTICE_MESSAGE = 'authNoticeMessage';
const SESSION_ENDED_MESSAGE = 'Your session has ended. Please sign in again.';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 100000, // 100 seconds
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

function getAlternateLoopbackBaseUrl(url?: string): string | null {
  if (!url) {
    return null;
  }

  try {
    const parsed = new URL(url);
    if (parsed.hostname === 'localhost') {
      parsed.hostname = '127.0.0.1';
      return parsed.toString().replace(/\/$/, '');
    }
    if (parsed.hostname === '127.0.0.1') {
      parsed.hostname = 'localhost';
      return parsed.toString().replace(/\/$/, '');
    }
  } catch {
    // Ignore malformed URLs and skip retry.
  }

  return null;
}

const AUTH_401_REDIRECT_EXCLUDE_PATHS = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
  '/api/auth/verify-email',
  '/api/auth/resend-verification',
  '/api/auth/mfa/verify',
  '/api/auth/mfa/resend',
];

function shouldHandle401WithRedirect(error: any): boolean {
  const requestUrl: string | undefined = error?.config?.url;

  if (typeof requestUrl === 'string') {
    const isExcludedAuthPath = AUTH_401_REDIRECT_EXCLUDE_PATHS.some((path) =>
      requestUrl === path || requestUrl.endsWith(path)
    );

    if (isExcludedAuthPath) {
      return false;
    }
  }

  // Avoid hard-refreshing when already on an auth route.
  if (typeof window !== 'undefined' && window.location.pathname === '/login') {
    return false;
  }

  return true;
}

function isStoredSuperAdmin(): boolean {
  try {
    const parsedUser = getStoredAuthUser<StoredUserContext>();
    return String(parsedUser?.role || '').trim().toLowerCase() === 'superadmin';
  } catch {
    return false;
  }
}

function handleServiceMaintenanceRedirect(error: any): void {
  if (typeof window === 'undefined' || error.response?.status !== 503) {
    return;
  }
  const detail = error.response?.data?.detail;
  if (!detail || detail.code !== 'service_maintenance' || isStoredSuperAdmin()) {
    return;
  }

  const serviceKey = String(detail.service_key || 'service');
  try {
    sessionStorage.setItem('acceliqaMaintenanceNotice', JSON.stringify(detail));
  } catch {
    // Best effort only; the route can fall back to a generic service message.
  }

  const nextPath = `/maintenance/${encodeURIComponent(serviceKey)}`;
  if (window.location.pathname !== nextPath) {
    window.location.href = nextPath;
  }
}

function logApiError(error: any, label: string = 'API request failed'): void {
  if (process.env.NODE_ENV === 'production') {
    return;
  }

  console.error(label, {
    status: error?.response?.status,
    method: error?.config?.method,
    message: error?.message,
  });
}

// Request interceptor for adding auth tokens if needed
apiClient.interceptors.request.use(
  (config) => {
    const token = getStoredAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const explicitActiveOrganizationId = config.headers?.[ACTIVE_ORGANIZATION_HEADER];
    const activeOrganizationId = getStoredActiveOrganizationId();
    if (explicitActiveOrganizationId) {
      return config;
    }

    if (activeOrganizationId) {
      config.headers[ACTIVE_ORGANIZATION_HEADER] = String(activeOrganizationId);
    } else if (config.headers && ACTIVE_ORGANIZATION_HEADER in config.headers) {
      delete config.headers[ACTIVE_ORGANIZATION_HEADER];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const canRetryLoopback =
      error?.message === 'Network Error'
      && error?.config
      && !error?.config?.headers?.['X-Loopback-Retry'];

    if (canRetryLoopback) {
      const alternateBaseUrl = getAlternateLoopbackBaseUrl(apiClient.defaults.baseURL);
      if (alternateBaseUrl) {
        try {
          const retryConfig = {
            ...error.config,
            baseURL: alternateBaseUrl,
            headers: {
              ...(error.config.headers || {}),
              'X-Loopback-Retry': '1',
            },
          };
          return await apiClient.request(retryConfig);
        } catch (retryError) {
          logApiError(retryError, 'API retry failed');
        }
      }
    }
    
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && shouldHandle401WithRedirect(error)) {
      try {
        sessionStorage.setItem(AUTH_NOTICE_MESSAGE, SESSION_ENDED_MESSAGE);
      } catch {
        // Best effort only; clearing auth and redirecting remains authoritative.
      }
      clearAuthSession();
      window.location.href = '/login';
    }

    const detail = error.response?.data?.detail;
    if (error.response?.status === 403 && detail?.code === 'mfa_required') {
      clearAuthSession();
      window.location.href = '/login';
    }

    handleServiceMaintenanceRedirect(error);

    if (error?.message === 'Network Error') {
      error.message = SAFE_NETWORK_ERROR_MESSAGE;
    }

    logApiError(error);
    
    return Promise.reject(error);
  }
);

export interface LLMModelInfo {
  id: string;
  display_name: string;
  input_cost_per_million?: string | null;
  output_cost_per_million?: string | null;
  context_window?: number | null;
  max_output?: number | null;
  /** "yes" = always available, "no" = never, "conditional" = only when reasoning_effort is null/"none" */
  supports_temperature: 'yes' | 'no' | 'conditional';
  supports_reasoning: boolean;
  reasoning_effort_options: string[];
  reasoning_effort_default?: string | null;
}

export interface LLMConfigData {
  subscription_mode?: 'platform' | 'byo_key';
  provider?: string;
  model: string;
  temperature: number;
  reasoning_effort?: string | null;
  provider_keys?: LLMProviderKeyItem[];
  provider_statuses?: LLMProviderStatusItem[];
  can_configure_byo?: boolean;
  can_configure_platform?: boolean;
  can_edit_model_defaults?: boolean;
  can_edit_route_overrides?: boolean;
  can_edit_provider_keys?: boolean;
  has_stored_byo_keys?: boolean;
  platform_mode_locked?: boolean;
  platform_mode_lock_reason?: string | null;
}

export interface LLMProviderKeyItem {
  provider: string;
  api_key: string;
  is_configured: boolean;
}

export interface LLMProviderStatusItem {
  provider: string;
  platform_configured: boolean;
  byo_configured: boolean;
  available_in_current_mode: boolean;
  unavailable_reason?: string | null;
}

export interface LLMRouteConfigItem {
  route_key: string;
  label: string;
  description?: string;
  is_configured: boolean;
  is_inherited?: boolean;
  inherited_from_route?: string | null;
  provider?: string | null;
  model?: string | null;
  temperature?: number | null;
  reasoning_effort?: string | null;
  effective_provider: string;
  effective_model: string;
  effective_temperature: number;
  effective_reasoning_effort?: string | null;
}

export interface LLMRoutesData {
  routes: LLMRouteConfigItem[];
  can_configure_byo?: boolean;
  can_configure_platform?: boolean;
}

export interface LLMRouteUpdateItem {
  route_key: string;
  enabled: boolean;
  provider?: string;
  model?: string;
  temperature?: number;
  reasoning_effort?: string | null;
}

export interface GlobalBillingSettingsData {
  stripe_secret_key_configured: boolean;
  stripe_secret_key: string;
  stripe_webhook_secret_configured: boolean;
  stripe_webhook_secret: string;
  stripe_publishable_key: string;
  llm_token_profit_percentage: number;
  llm_usage_recharge_amount_cents: number;
  llm_usage_recharge_amount_usd: number;
  llm_usage_auto_recharge_threshold_cents: number;
  llm_usage_auto_recharge_threshold_usd: number;
  llm_usage_currency: string;
}

export interface GlobalBillingSettingsUpdate {
  stripe_secret_key?: string;
  stripe_webhook_secret?: string;
  stripe_publishable_key?: string;
  llm_token_profit_percentage?: number;
  llm_usage_recharge_amount_cents?: number;
  llm_usage_auto_recharge_threshold_cents?: number;
  llm_usage_currency?: string;
}

export default apiClient;

// API endpoints
export const API_ENDPOINTS = {
  // Authentication
  LOGIN: '/api/auth/login',
  REGISTER: '/api/auth/register',
  FORGOT_PASSWORD: '/api/auth/forgot-password',
  RESET_PASSWORD: '/api/auth/reset-password',
  VERIFY_EMAIL: '/api/auth/verify-email',
  RESEND_VERIFICATION: '/api/auth/resend-verification',
  MFA_VERIFY: '/api/auth/mfa/verify',
  MFA_RESEND: '/api/auth/mfa/resend',
  MFA_TRUSTED_DEVICES: '/api/auth/mfa/trusted-devices',
  LOGOUT: '/api/auth/logout',
  ME: '/api/auth/me',
  
  // Projects
  PROJECTS: '/api/projects',
  PROJECTS_WITH_KNOWLEDGE: '/api/projects/with-knowledge',
  PROJECT_DETAIL: (id: number | string) => `/api/projects/${id}`,
  PROJECT_ANALYTICS: (id: number | string) => `/api/projects/${id}/analytics`,
  PROJECT_RENAME: (id: number | string) => `/api/projects/${id}/name`,
  PROJECT_DELETION_IMPACT: (id: number | string) => `/api/projects/${id}/deletion-impact`,
  PROJECT_REPOSITORY: (id: number | string) => `/api/projects/${id}/repository`,
  PROJECT_AZURE_REPO_INTEGRATION: (id: number | string) => `/api/${id}/azure-repo-integration`,
  PROJECT_DOMAIN_KNOWLEDGE: (id: number | string) => `/api/projects/${id}/domain-knowledge`,
  PROJECT_DOMAIN_KNOWLEDGE_REFRESH: (id: number | string) => `/api/projects/${id}/domain-knowledge/refresh`,
  PROJECT_DOMAIN_KNOWLEDGE_FOCUS_AREAS: (id: number | string) => `/api/projects/${id}/domain-knowledge/focus-areas`,
  PROJECT_ENTERPRISE_KNOWLEDGE: (id: number | string) => `/api/projects/${id}/enterprise-knowledge`,
  PROJECT_ENTERPRISE_KNOWLEDGE_REFRESH: (id: number | string) => `/api/projects/${id}/enterprise-knowledge/refresh`,
  PROJECT_REQUIREMENT_DOCUMENTS: (id: number | string) => `/api/projects/${id}/requirements/documents`,
  PROJECT_REQUIREMENT_DOCUMENT: (projectId: number | string, documentId: number | string) => `/api/projects/${projectId}/requirements/documents/${documentId}`,
  PROJECT_REQUIREMENT_DOCUMENT_DOWNLOAD: (projectId: number | string, documentId: number | string) => `/api/projects/${projectId}/requirements/documents/${documentId}/download`,
  PROJECT_REQUIREMENT_DOCUMENT_REPROCESS: (projectId: number | string, documentId: number | string) => `/api/projects/${projectId}/requirements/documents/${documentId}/reprocess`,
  PROJECT_REQUIREMENT_ADO_WORK_ITEMS: (id: number | string) => `/api/projects/${id}/requirements/ado-work-items`,
  PROJECT_REQUIREMENT_REVIEW: (id: number | string) => `/api/projects/${id}/requirements/review`,
  PROJECT_REQUIREMENT_ADO_NOTES: (id: number | string) => `/api/projects/${id}/requirements/ado-notes`,
  PROJECT_REQUIREMENT_GENERATIONS: (projectId: number | string) => `/api/projects/${projectId}/requirements/test-generations`,
  PROJECT_REQUIREMENT_GENERATION_PREFLIGHT: (projectId: number | string) => `/api/projects/${projectId}/requirements/test-generations/preflight`,
  PROJECT_REQUIREMENT_GENERATION: (projectId: number | string, generationId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}`,
  PROJECT_REQUIREMENT_GENERATION_ARTIFACT_DOWNLOAD: (projectId: number | string, generationId: number | string, artifactId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/download`,
  PROJECT_REQUIREMENT_GENERATION_ARTIFACT_REQUIREMENTS: (projectId: number | string, generationId: number | string, artifactId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/requirements`,
  PROJECT_REQUIREMENT_TEST_WORKSPACE: (projectId: number | string, generationId: number | string, artifactId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/test-workspace`,
  PROJECT_REQUIREMENT_LIVE_TEST_WORKSPACE: (projectId: number | string, generationId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/live-test-workspace`,
  PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_PUSH: (projectId: number | string, generationId: number | string, artifactId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/test-workspace/ado-push`,
  PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_PUSH_START: (projectId: number | string, generationId: number | string, artifactId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/test-workspace/ado-push/start`,
  PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_PUSH_JOB: (projectId: number | string, generationId: number | string, artifactId: number | string, pushJobId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/test-workspace/ado-push/jobs/${pushJobId}`,
  PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_PUSH_LATEST: (projectId: number | string, generationId: number | string, artifactId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/test-workspace/ado-push/jobs/latest`,
  PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_COMPARE: (projectId: number | string, generationId: number | string, artifactId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/test-workspace/ado-compare`,
  PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_COMPARE_SYNC: (projectId: number | string, generationId: number | string, artifactId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/test-workspace/ado-compare/sync`,
  PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_COMPARE_SYNC_BULK: (projectId: number | string, generationId: number | string, artifactId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/test-workspace/ado-compare/sync-bulk`,
  PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_COMPARE_SYNC_START: (projectId: number | string, generationId: number | string, artifactId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/test-workspace/ado-compare/sync-bulk/start`,
  PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_COMPARE_SYNC_JOB: (projectId: number | string, generationId: number | string, artifactId: number | string, syncJobId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/test-workspace/ado-compare/sync-jobs/${syncJobId}`,
  PROJECT_REQUIREMENT_TEST_WORKSPACE_ADO_COMPARE_SYNC_LATEST: (projectId: number | string, generationId: number | string, artifactId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/test-workspace/ado-compare/sync-jobs/latest`,
  PROJECT_REQUIREMENT_TEST_WORKSPACE_TEST: (projectId: number | string, generationId: number | string, artifactId: number | string, testKey: string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/test-workspace/tests/${encodeURIComponent(testKey)}`,
  PROJECT_REQUIREMENT_TEST_WORKSPACE_TESTS: (projectId: number | string, generationId: number | string, artifactId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/test-workspace/tests`,
  PROJECT_REQUIREMENT_TEST_WORKSPACE_TEST_FOLDER: (projectId: number | string, generationId: number | string, artifactId: number | string, testKey: string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/test-workspace/tests/${encodeURIComponent(testKey)}/folder`,
  PROJECT_REQUIREMENT_TEST_WORKSPACE_FOLDERS: (projectId: number | string, generationId: number | string, artifactId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/test-workspace/folders`,
  PROJECT_REQUIREMENT_TEST_WORKSPACE_FOLDER_RENAME: (projectId: number | string, generationId: number | string, artifactId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/test-workspace/folders/rename`,
  PROJECT_REQUIREMENT_TEST_CASE_VERSIONS: (projectId: number | string, tcKey: string) => `/api/projects/${projectId}/requirements/test-cases/${encodeURIComponent(tcKey)}/versions`,
  PROJECT_REQUIREMENT_GENERATE_GHERKIN: (projectId: number | string, generationId: number | string, artifactId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/generate-gherkin`,
  PROJECT_REQUIREMENT_GHERKIN_STATUS: (projectId: number | string, generationId: number | string, artifactId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/gherkin-status`,
  PROJECT_REQUIREMENT_GHERKIN_RUNS: (projectId: number | string, generationId: number | string, artifactId: number | string) => `/api/projects/${projectId}/requirements/test-generations/${generationId}/artifacts/${artifactId}/gherkin-runs`,
  PROJECT_REQUIREMENT_GHERKIN_DOWNLOAD: (projectId: number | string, gherkinJobId: number | string, artifactId: number | string) => `/api/projects/${projectId}/requirements/gherkin-jobs/${gherkinJobId}/artifacts/${artifactId}/download`,

  // Chat
  CHAT_SESSIONS: '/api/chat/sessions',
  CHAT_SESSION_MESSAGE: (sessionId: string) => `/api/chat/sessions/${sessionId}/message`,
  CHAT_SESSION_MESSAGE_STREAM: (sessionId: string) => `/api/chat/sessions/${sessionId}/message/stream`,
  CHAT_SESSION_HISTORY: (sessionId: string) => `/api/chat/sessions/${sessionId}/history`,
  CHAT_SESSION_DELETE: (sessionId: string) => `/api/chat/sessions/${sessionId}`,

  // Exploratory Agent
  EXPLORATORY_AGENT_RUNS: '/api/exploratory-agent/runs',
  EXPLORATORY_AGENT_RUN: (runId: string) => `/api/exploratory-agent/runs/${encodeURIComponent(runId)}`,
  EXPLORATORY_AGENT_APPROVE: (runId: string) => `/api/exploratory-agent/runs/${encodeURIComponent(runId)}/approve`,
  EXPLORATORY_AGENT_CANCEL: (runId: string) => `/api/exploratory-agent/runs/${encodeURIComponent(runId)}/cancel`,
  EXPLORATORY_AGENT_ARTIFACT: (runId: string, filename: string) => `/api/exploratory-agent/runs/${encodeURIComponent(runId)}/artifacts/${encodeURIComponent(filename)}`,

  // Fixed Ritz reservation demonstration
  RITZ_RESERVATION_DEMO_RUNS: '/api/ritz-reservation-demo/runs',
  RITZ_RESERVATION_DEMO_RUN: (runId: string) => `/api/ritz-reservation-demo/runs/${encodeURIComponent(runId)}`,
  RITZ_RESERVATION_DEMO_CANCEL: (runId: string) => `/api/ritz-reservation-demo/runs/${encodeURIComponent(runId)}/cancel`,

  // CSV batch Ritz reservations
  RITZ_BATCH_RUNS: '/api/ritz-batch/batches',
  RITZ_BATCH_RUN: (batchId: string) => `/api/ritz-batch/batches/${encodeURIComponent(batchId)}`,
  RITZ_BATCH_OUTPUT: (batchId: string) => `/api/ritz-batch/batches/${encodeURIComponent(batchId)}/output`,

  // Dashboard
  DASHBOARD_DATA: '/api/dashboard/get-dashboard-data',
  KPIS: '/api/dashboard/kpis',

  // Repository management
  UPLOAD_REPO: '/api/repositories/upload',
  REPOSITORIES: '/api/repositories',
  REPOSITORY_DETAILS: (id: string) => `/api/repositories/${id}`,
  GENERATE_TESTS: (id: string) => `/api/repositories/${id}/generate-tests`,
  REPOSITORY_LOGS: (id: string) => `/api/repositories/${id}/logs`,
  REPOSITORY_INPUT_FILES: (id: string) => `/api/repositories/${id}/files/input`,
  REPOSITORY_OUTPUT_FILES: (id: string) => `/api/repositories/${id}/files/output`,
  REPOSITORY_FILE_CONTENT: (id: string, scope: 'input' | 'output', path: string) => `/api/repositories/${id}/file-content?scope=${encodeURIComponent(scope)}&path=${encodeURIComponent(path)}`,
  REPOSITORY_METRICS: (id: string) => `/api/repositories/${id}/metrics`,
  REPOSITORY_RESULTS: (id: string) => `/api/repositories/${id}/results`,
  REPOSITORY_RESULTS_REFRESH: (id: string) => `/api/repositories/${id}/results/refresh`,
  REPOSITORY_TEST_GENERATIONS: (id: string) => `/api/repositories/${id}/test-generations`,
  REPOSITORY_DOWNLOAD_OUTPUT_ZIP: (id: string) => `/api/repositories/${id}/download/output`,
  REPOSITORY_DOWNLOAD_INPUT_ZIP: (id: string) => `/api/repositories/${id}/download/input-zip`,
  REPOSITORY_AZURE_REPO_URL: '/api/repositories/azure-repo-url',
  REPOS_BULK_DELETE: '/api/repositories/bulk-delete',
  REPOS_BULK_STATUS: '/api/repositories/bulk-status',
  TEST_GENERATIONS: '/api/test-generations',
  DELETE_TEST_GENERATION: (id: number | string) => `/api/test-generations/${id}`,
  TEST_GENERATION_LOGS: (id: number | string) => `/api/test-generations/${id}/logs`,
  TEST_GENERATION_LIVE_LOG: (id: number | string) => `/api/test-generations/${id}/live-log`,
  CANCEL_TEST_GENERATION: (id: number | string) => `/api/test-generations/${id}/cancel`,
  
  // Playwright runner
  PLAYWRIGHT_CREATE_JOB: (projectId: number | string) => `/api/projects/${projectId}/playwright/jobs`,
  PLAYWRIGHT_CREATE_JOB_WITH_FILES: (projectId: number | string) => `/api/projects/${projectId}/playwright/jobs/with-files`,
  PLAYWRIGHT_RERUN_TEST: (projectId: number | string, jobId: number | string) => `/api/projects/${projectId}/playwright/jobs/${jobId}/rerun-test`,
  PLAYWRIGHT_TEST_AUTH_MODE: (projectId: number | string, artifactId: number | string, testKey: string) => `/api/projects/${projectId}/playwright/artifacts/${artifactId}/tests/${encodeURIComponent(testKey)}/auth-mode`,
  PLAYWRIGHT_JOBS: (projectId: number | string) => `/api/projects/${projectId}/playwright/jobs`,
  PLAYWRIGHT_JOB: (projectId: number | string, jobId: number | string) => `/api/projects/${projectId}/playwright/jobs/${jobId}`,
  PLAYWRIGHT_RENAME_JOB: (projectId: number | string, jobId: number | string) => `/api/projects/${projectId}/playwright/jobs/${jobId}/name`,
  PLAYWRIGHT_JOB_SUMMARY: (projectId: number | string, jobId: number | string) => `/api/projects/${projectId}/playwright/jobs/${jobId}/summary`,
  PLAYWRIGHT_CANCEL_JOB: (projectId: number | string, jobId: number | string) => `/api/projects/${projectId}/playwright/jobs/${jobId}`,
  PLAYWRIGHT_RUN_FILE: (projectId: number | string, runId: string, relativePath: string) => `/api/projects/${projectId}/playwright/runs/${encodeURIComponent(runId)}/files/${relativePath}`,
  // Executions page — reuses the jobs list / detail endpoints
  PLAYWRIGHT_EXECUTIONS: (projectId: number | string) => `/api/projects/${projectId}/playwright/jobs`,
  PLAYWRIGHT_EXECUTION_DETAIL: (projectId: number | string, jobId: number | string) => `/api/projects/${projectId}/playwright/jobs/${jobId}`,
  PLAYWRIGHT_EXECUTION_SUMMARY: (projectId: number | string, jobId: number | string) => `/api/projects/${projectId}/playwright/jobs/${jobId}/summary`,
  PLAYWRIGHT_ADO_SYNC: (projectId: number | string, jobId: number | string) => `/api/projects/${projectId}/playwright/jobs/${jobId}/ado-sync`,
  PLAYWRIGHT_ADO_VALIDATE: (projectId: number | string) => `/api/projects/${projectId}/playwright/ado-test-plan/validate`,
  PLAYWRIGHT_TEST_EVIDENCE: (projectId: number | string, jobId: number | string, testKey: string, evidenceKey?: string) => {
    const base = `/api/projects/${projectId}/playwright/jobs/${jobId}/tests/${encodeURIComponent(testKey)}/evidence`;
    return evidenceKey ? `${base}?evidence_key=${encodeURIComponent(evidenceKey)}` : base;
  },
  // Resolved actions (step cache per artifact)
  PLAYWRIGHT_RESOLVED_ACTIONS: (projectId: number | string, artifactId: number | string, jobId?: number | string) =>
    `/api/projects/${projectId}/playwright/artifacts/${artifactId}/resolved-actions${jobId ? `?job_id=${encodeURIComponent(String(jobId))}` : ''}`,
  PLAYWRIGHT_PATCH_TEST_ACTIONS: (projectId: number | string, artifactId: number | string, testKey: string) =>
    `/api/projects/${projectId}/playwright/artifacts/${artifactId}/resolved-actions/${encodeURIComponent(testKey)}`,
  PLAYWRIGHT_RUN_PREVIEW: (projectId: number | string, artifactId: number | string) =>
    `/api/projects/${projectId}/playwright/artifacts/${artifactId}/run-preview`,

  // Test Data
  TEST_DATA_LIST: (projectId: number | string) => `/api/projects/${projectId}/test-data`,
  TEST_DATA_DETAIL: (projectId: number | string, datasetId: string) => `/api/projects/${projectId}/test-data/${datasetId}`,
  TEST_DATA_RENAME: (projectId: number | string, datasetId: string) => `/api/projects/${projectId}/test-data/${datasetId}`,
  TEST_DATA_DELETE: (projectId: number | string, datasetId: string) => `/api/projects/${projectId}/test-data/${datasetId}`,
  TEST_DATA_DOWNLOAD: (projectId: number | string, datasetId: string) => `/api/projects/${projectId}/test-data/${datasetId}/download`,
  TEST_DATA_GENERATE: (projectId: number | string) => `/api/projects/${projectId}/test-data/generate`,
  TEST_DATA_MATCH_PREVIEW: (projectId: number | string) => `/api/projects/${projectId}/test-data/match-preview`,
  TEST_DATA_CASE_VARIANTS: (projectId: number | string, tcKey: string) => `/api/projects/${projectId}/test-data/test-cases/${encodeURIComponent(tcKey)}/variants`,
  TEST_DATA_ROWS_UPDATE: (projectId: number | string, datasetId: string) => `/api/projects/${projectId}/test-data/${datasetId}/rows`,
  
  // User management
  PROFILE: '/api/profile',
  UPDATE_PROFILE: '/api/profile',
  CHANGE_PASSWORD: '/api/auth/change-password',
  SETUP_ORGANIZATION: '/api/auth/setup-organization',
  TEAMS: '/api/teams',
  USERS: '/api/users',
  USERS_ADD_TO_ORGANIZATION: '/api/users/add-to-organization',
  USER_DETAIL: (id: number | string) => `/api/users/${id}`,
  USERS_BULK_DELETE: '/api/users/bulk-delete',
  USERS_IMPORT_CSV: '/api/users/import-csv',

  // Organization management
  ORGANIZATIONS: '/api/organizations',
  ORGANIZATION_DETAIL: (id: number | string) => `/api/organizations/${id}`,
  ORGANIZATION_OVERVIEW: (id: number | string) => `/api/organizations/${id}/overview`,
  ORGANIZATION_USERS_BY_ID: (id: number | string) => `/api/organizations/${id}/users`,
  ORGANIZATION_TEAMS_BY_ID: (id: number | string) => `/api/organizations/${id}/teams`,
  ORGANIZATION_ME: '/api/organizations/me',
  ORGANIZATION_USERS: '/api/organizations/organization-users',
  ORGANIZATION_USAGE: '/api/llm-usage/organization',

  // Organization feature flags
  ORGANIZATION_FEATURE_FLAGS_ACTIVE: '/api/organizations/feature-flags/active',

  // Configuration
  LLM_CONFIG: '/api/config/llm',
  LLM_PROVIDER_KEY: '/api/config/llm/provider-key',
  LLM_ROUTES: '/api/config/llm/routes',
  LLM_MODELS: '/api/config/models',
  PROMPTS: '/api/config/prompts',
  ORG_PROMPT_INSTRUCTIONS: '/api/config/org-prompt-instructions',
  PUBLIC_CONFIG: '/api/config/public',
  VECTOR_INGESTION_KEY: '/api/config/vector-ingestion-key',
  GLOBAL_BILLING_SETTINGS: '/api/config/billing-settings',
}; 

// Config API helpers
export const getLLMConfig = async (): Promise<LLMConfigData> => {
  const response = await apiClient.get<LLMConfigData>(API_ENDPOINTS.LLM_CONFIG);
  return response.data;
};

export const getLLMModels = async (provider?: string): Promise<LLMModelInfo[]> => {
  const response = await apiClient.get<{ models?: LLMModelInfo[] }>(API_ENDPOINTS.LLM_MODELS, {
    params: provider ? { provider } : undefined,
  });
  return response.data?.models || [];
};

export const getLLMProviderKey = async (provider: string): Promise<{ provider: string; api_key: string }> => {
  const response = await apiClient.get<{ provider: string; api_key: string }>(API_ENDPOINTS.LLM_PROVIDER_KEY, {
    params: { provider },
  });
  return response.data;
};

export const updateLLMConfig = async (payload: {
  subscription_mode?: 'platform' | 'byo_key';
  provider?: string;
  provider_keys?: Record<string, string>;
  byo_key_retention?: 'keep' | 'delete';
  model?: string;
  temperature?: number;
  reasoning_effort?: string | null;
}): Promise<LLMConfigData> => {
  const response = await apiClient.put<LLMConfigData>(API_ENDPOINTS.LLM_CONFIG, payload);
  return response.data;
};

export const getLLMRoutes = async (): Promise<LLMRoutesData> => {
  const response = await apiClient.get<LLMRoutesData>(API_ENDPOINTS.LLM_ROUTES);
  return response.data;
};

export const updateLLMRoutes = async (routes: LLMRouteUpdateItem[]): Promise<LLMRoutesData> => {
  const response = await apiClient.put<LLMRoutesData>(API_ENDPOINTS.LLM_ROUTES, { routes });
  return response.data;
};

// Legacy page compatibility helpers
export interface OrganizationUsageByUser {
  user_id: number | null;
  email: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  provider_cost_usd?: number;
  customer_charge_usd?: number;
  profit_usd?: number;
  job_count: number;
}

export interface OrganizationUsageByModel {
  model: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  provider_cost_usd?: number;
  customer_charge_usd?: number;
  profit_usd?: number;
  job_count: number;
}

export interface OrganizationUsage {
  organization_id: number;
  period_days?: number | null;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  provider_cost_usd?: number;
  customer_charge_usd?: number;
  profit_usd?: number;
  job_count: number;
  by_user: OrganizationUsageByUser[];
  by_model: OrganizationUsageByModel[];
}

export async function getOrganizationUsage(days?: number): Promise<OrganizationUsage> {
  const response = await apiClient.get<OrganizationUsage>(API_ENDPOINTS.ORGANIZATION_USAGE, {
    params: days ? { days } : {},
  });
  return response.data;
}

// ---------------------------------------------------------------------------
// Org custom prompt instructions
// ---------------------------------------------------------------------------

export interface OrgPromptInstructionItem {
  agent_name: string;
  custom_instructions: string | null;
  prompt_type: string | null;
}

export interface OrgPromptInstructionsResponse {
  instructions: OrgPromptInstructionItem[];
}

export async function getOrgPromptInstructions(): Promise<OrgPromptInstructionsResponse> {
  const response = await apiClient.get<OrgPromptInstructionsResponse>(
    API_ENDPOINTS.ORG_PROMPT_INSTRUCTIONS,
  );
  return response.data;
}

export async function updateOrgPromptInstructions(
  instructions: OrgPromptInstructionItem[],
): Promise<OrgPromptInstructionsResponse> {
  const response = await apiClient.put<OrgPromptInstructionsResponse>(
    API_ENDPOINTS.ORG_PROMPT_INSTRUCTIONS,
    { instructions },
  );
  return response.data;
}

export async function getVectorIngestionKeyStatus(): Promise<{ is_configured: boolean; api_key: string }> {
  const response = await apiClient.get<{ is_configured: boolean; api_key: string }>(API_ENDPOINTS.VECTOR_INGESTION_KEY);
  return response.data;
}

export async function updateVectorIngestionKey(api_key: string): Promise<{ is_configured: boolean; api_key: string }> {
  const response = await apiClient.put<{ is_configured: boolean; api_key: string }>(API_ENDPOINTS.VECTOR_INGESTION_KEY, { api_key });
  return response.data;
}

export async function getGlobalBillingSettings(): Promise<GlobalBillingSettingsData> {
  const response = await apiClient.get<GlobalBillingSettingsData>(API_ENDPOINTS.GLOBAL_BILLING_SETTINGS);
  return response.data;
}

export async function updateGlobalBillingSettings(payload: GlobalBillingSettingsUpdate): Promise<GlobalBillingSettingsData> {
  const response = await apiClient.put<GlobalBillingSettingsData>(API_ENDPOINTS.GLOBAL_BILLING_SETTINGS, payload);
  return response.data;
}
